/**
 * StoneSlide custom element for displaying stones in a slider
 * Similar to product-slide but designed for stone-card components
 * 
 * @class StoneSlide
 * @extends HTMLElement
 */
class StoneSlide extends HTMLElement {
  /**
   * Creates an instance of StoneSlide.
   * Initializes the Swiper instance and state tracking variables.
   */
  constructor() {
    super();
    /** @private {Swiper|null} Reference to the Swiper instance */
    this.swiper = null;
    /** @private {boolean} Flag to track initialization state */
    this.isInitialized = false;
    /** @private {number|null} Reference to the interval check */
    this.swiperCheckInterval = null;
  }

  /**
   * Lifecycle callback when the element is added to the DOM.
   * Ensures Swiper is only initialized after the page and required resources are loaded.
   * @override
   */
  connectedCallback() {
    if (this.isInitialized) return;

    // Setup structure if not already done (e.g., if addStoneCard was called first)
    if (!this.querySelector('.swiper')) {
      this.setupSliderStructure();
    }

    // Wait for both window load and Swiper to be available
    if (document.readyState === 'complete') {
      this.waitForSwiper();
    } else {
      window.addEventListener('load', () => this.waitForSwiper(), { once: true });
    }
  }

  /**
   * Lifecycle callback when the element is removed from the DOM.
   * Cleans up Swiper instance.
   * @override
   */
  disconnectedCallback() {
    this.destroySwiper();
    if (this.swiperCheckInterval) {
      clearInterval(this.swiperCheckInterval);
      this.swiperCheckInterval = null;
    }
  }

  /**
   * Waits for the Swiper library to be available before initialization.
   * Implements a polling mechanism with a timeout.
   * @private
   */
  waitForSwiper() {
    if (this.isInitialized) return;

    // Clear any existing interval
    if (this.swiperCheckInterval) {
      clearInterval(this.swiperCheckInterval);
      this.swiperCheckInterval = null;
    }

    // Check if Swiper is available
    if (typeof Swiper !== 'undefined') {
      this.initSwiper();
      this.isInitialized = true;
      this.dataset.ready = true;
      return;
    }

    // Poll for Swiper availability
    let attempts = 0;
    const maxAttempts = 50; // 5 seconds with 100ms interval

    this.swiperCheckInterval = setInterval(() => {
      attempts++;
      
      if (typeof Swiper !== 'undefined') {
        clearInterval(this.swiperCheckInterval);
        this.swiperCheckInterval = null;
        this.initSwiper();
        this.isInitialized = true;
        this.dataset.ready = true;
      } else if (attempts >= maxAttempts) {
        clearInterval(this.swiperCheckInterval);
        this.swiperCheckInterval = null;
        console.error('Swiper failed to load within timeout period');
      }
    }, 100);
  }

  /**
   * Sets up the HTML structure required for the Swiper slider.
   * @private
   */
  setupSliderStructure() {
    // Only setup if not already done
    if (this.querySelector('.swiper')) return;

    // Add overflow-hidden to the parent container
    this.className = 'overflow-hidden block w-full';

    // Add title if provided
    if (this.dataset.title && !this.querySelector('.stone-slide__title')) {
      const titleElement = document.createElement('h2');
      titleElement.className = 'stone-slide__title text-2xl tablet:text-4xl inline-block';
      titleElement.textContent = this.dataset.title;

      // Create a container for title
      const titleContainer = document.createElement('div');
      titleContainer.className = 'flex items-end justify-between gap-10 px-4 mb-5 mt-5 tablet:justify-start';
      titleContainer.appendChild(titleElement);

      this.insertBefore(titleContainer, this.firstChild);
    }

    // Create wrapper and container for Swiper
    const swiperWrapper = document.createElement('div');
    swiperWrapper.className = 'swiper overflow-visible px-4';

    const swiperContainer = document.createElement('div');
    swiperContainer.className = 'swiper-wrapper';

    // Move existing stone-card elements into the swiper container
    const items = Array.from(this.children).filter(
      child => child.tagName?.toLowerCase() === 'legacy-stone-card'
    );
    
    items.forEach(item => {
      const slideWrapper = document.createElement('div');
      slideWrapper.className = 'swiper-slide';
      slideWrapper.appendChild(item);
      swiperContainer.appendChild(slideWrapper);
    });

    swiperWrapper.appendChild(swiperContainer);

    // Add navigation
    const prevButton = document.createElement('div');
    prevButton.className = 'swiper-button-prev hidden -top-[40px] right-[70px] left-auto tablet:flex';
    const nextButton = document.createElement('div');
    nextButton.className = 'swiper-button-next hidden -top-[40px] right-[20px] left-auto tablet:flex';

    swiperWrapper.appendChild(prevButton);
    swiperWrapper.appendChild(nextButton);

    this.appendChild(swiperWrapper);
  }

  /**
   * Getter for Swiper configuration options.
   * @returns {Object} Configuration object for Swiper initialization
   * @private
   */
  get swiperConfig() {
    return {
      slidesPerView: 1.6, // Mobile default
      spaceBetween: 20,
      navigation: {
        nextEl: '.swiper-button-next',
        prevEl: '.swiper-button-prev',
      },
      breakpoints: {
        // Tablet
        768: {
          slidesPerView: 3,
        },
        // Desktop
        1024: {
          slidesPerView: 4,
        }
      }
    };
  }

  /**
   * Initializes the Swiper instance with the configured options.
   * @private
   */
  initSwiper() {
    if (this.swiper) return;

    const swiperElement = this.querySelector('.swiper');

    if (swiperElement) {
      this.swiper = new Swiper(swiperElement, this.swiperConfig);
    }
  }

  /**
   * Destroys the Swiper instance and cleans up references.
   * @private
   */
  destroySwiper() {
    if (this.swiper) {
      this.swiper.destroy(true, true);
      this.swiper = null;
    }
  }

  /**
   * Adds a stone card to the slider
   * @param {Object} stoneData - The stone data object
   * @param {string} stoneType - The type of stone (diamond, moissanite, gemstone)
   */
  addStoneCard(stoneData, stoneType) {
    // Ensure slider structure is set up before adding cards
    if (!this.querySelector('.swiper')) {
      this.setupSliderStructure();
    }

    const swiperWrapper = this.querySelector('.swiper-wrapper');
    if (!swiperWrapper) {
      console.error('Swiper wrapper not found. Could not set up slider structure.');
      return;
    }

    const slideWrapper = document.createElement('div');
    slideWrapper.className = 'swiper-slide';

    const stoneCard = document.createElement('legacy-stone-card');
    stoneCard.setAttribute('stone-type', stoneType);
    stoneCard.stoneData = stoneData;

    slideWrapper.appendChild(stoneCard);
    swiperWrapper.appendChild(slideWrapper);

    // Update Swiper if it's already initialized
    if (this.swiper) {
      this.swiper.update();
    }
  }
}

// Register the web component
if (!customElements.get('stone-slide')) {
  customElements.define('stone-slide', StoneSlide);
}
